import { getGeneralContentLibrary, getTeacherLibraryFolders, getTeacherLibraryItems } from 'core/crud/library.crud';
import { canUseTangerineViewerInPreview, getIconByContentType } from 'core/utils/lessonItems';
import { isEditorial, isSchoolAdmin, isTeacher } from 'core/utils/user';
import moment from 'moment';

function getAvatarByRole(role, src) {
  if (isTeacher(role)) {
    return src;
  }
  if (isEditorial(role)) {
    return 'logo';
  }

  if (isSchoolAdmin(role)) {
    return 'assets/adminescuela.png';
  }
  return src;
}

function getNameByRole(role, name) {
  if (isTeacher(role)) {
    return name;
  }
  if (isEditorial(role)) {
    return 'Editorial';
  }
  if (isSchoolAdmin(role)) {
    return 'School Admin';
  }
  return name;
}

function getAppropiatedFor(item) {
  const educationLevels = item?.content_education_levels?.map((level) => level?.education_level_name) || [];
  const educationYears = item?.content_education_years?.map((year) => year?.education_year_name) || [];
  const disciplines = item?.content_disciplines?.map((discipline) => discipline?.discipline_name) || [];
  return [...educationLevels, ...educationYears, ...disciplines];
}

export async function getGeneralContentLibraryService(params) {
  const { page, page_size } = params;
  const response = await getGeneralContentLibrary(params);

  if (response?.data?.data?.length === 0) {
    return {
      data: [],
      total: 0,
      page: page,
      page_size: page_size,
      total_pages: 0,
    };
  }

  if (response?.data?.data?.length > 0) {
    const transformedData = response.data.data.map((item) => ({
      ...item,
      guid: item?.id,
      contentGuid: item?.content_guid,
      name: item?.content_name || item?.name || '',
      icon: getIconByContentType(item?.content_type),
      educationLevel: item?.education_level,
      visibility: item?.content_is_teacher_only ? 'Teacher' : 'Students',
      owner: getNameByRole(item?.author_role, item?.author_name),
      createdAt: moment(item?.created_at).format('DD/MM/YYYY'),
      selected: item?.in_teacher_library,
      avatar: getAvatarByRole(item?.author_role, item?.author_avatar),
      useTangerineViewerInPreview: canUseTangerineViewerInPreview(item?.content_type),
      appropiatedFor: getAppropiatedFor(item),
    }));

    return {
      data: transformedData,
      total: response.data.total,
      page: page,
      page_size: page_size,
      total_pages: Math.ceil(response.data.total / page_size),
    };
  }
}

export async function getTeacherLibraryItemsService(params, userGuid) {
  const { page, page_size } = params;
  const response = await getTeacherLibraryItems(params, userGuid);

  if (response?.data?.data?.length === 0) {
    return {
      data: [],
      total: 0,
      page: page,
      page_size: page_size,
      total_pages: 0,
    };
  }

  if (response?.data?.data?.length > 0) {
    const transformedData = response.data.data.map((item) => {
      // Extract library_item and rest of properties
      const { library_item, ...restItem } = item;

      return {
        ...restItem,
        guid: item?.id,
        contentGuid: item?.content?.guid,
        name: item?.content?.name || item?.name || '',
        icon: getIconByContentType(item?.content?.type),
        description: item?.content?.description,
        visibility: item?.content?.is_teacher_only ? 'Teacher' : 'Students',

        // Conditional fields based on library_item existence
        owner: library_item
          ? getNameByRole(library_item?.created_by_role, library_item?.created_by?.name || library_item?.created_by_name)
          : getNameByRole(item?.created_by_role, item?.created_by?.name),

        createdAt: library_item ? moment(library_item?.created_at).format('DD/MM/YYYY') : moment(item?.created_at).format('DD/MM/YYYY'),

        avatar: library_item
          ? getAvatarByRole(library_item?.created_by_role, library_item?.created_by?.avatar || library_item?.created_by_avatar)
          : getAvatarByRole(item?.created_by?.role, item?.created_by?.avatar),

        // Library item indicators
        isLibraryItem: !!library_item,
        libraryItemId: library_item?.id || null,

        selected: item?.in_teacher_library,
        useTangerineViewerInPreview: canUseTangerineViewerInPreview(item?.content_type),
        appropiatedFor: getAppropiatedFor(item),
      };
    });

    return {
      data: transformedData,
      total: response.data.total,
      page: page,
      page_size: page_size,
      total_pages: Math.ceil(response.data.total / page_size),
    };
  }
}

export async function getTeacherLibraryFoldersService(params, userGuid) {
  const response = await getTeacherLibraryFolders(params, userGuid);

  if (response?.data?.data?.length === 0) {
    return {
      data: [],
    };
  }
  if (response?.data?.data?.length > 0) {
    const transformedData = response.data.data.map((folder) => ({
      ...folder,
      items:
        folder.items?.map((item) => {
          // Extract library_item and rest of properties
          const { library_item, ...restItem } = item;

          return {
            ...restItem,
            guid: item?.id,
            contentGuid: item?.content?.guid,
            name: item?.content?.name || item?.name || '',
            icon: getIconByContentType(item?.content?.type),
            description: item?.content?.description,
            visibility: item?.content?.is_teacher_only ? 'Teacher' : 'Students',

            // Conditional fields based on library_item existence
            owner: library_item
              ? getNameByRole(library_item?.created_by_role, library_item?.created_by_name)
              : getNameByRole(item?.created_by?.role, item?.created_by?.name),

            createdAt: library_item ? moment(library_item?.created_at).format('DD/MM/YYYY') : moment(item?.created_at).format('DD/MM/YYYY'),

            avatar: library_item
              ? getAvatarByRole(library_item?.created_by_role, library_item?.created_by_avatar)
              : getAvatarByRole(item?.created_by?.role, item?.created_by?.avatar),

            // Library item indicators
            isLibraryItem: !!library_item,
            libraryItemId: library_item?.id || null,

            selected: item?.in_teacher_library,
            useTangerineViewerInPreview: canUseTangerineViewerInPreview(item?.content?.type),
            appropiatedFor: getAppropiatedFor(item),
          };
        }) || [],
    }));

    return {
      data: transformedData,
    };
  }
}
